/**
 * useNotificationRealtime Hook
 * Manages real-time notifications via Socket.IO
 * This hook should be used at the app level (Header) to ensure
 * notifications work on all pages, not just Orders page
 */

import { useCallback } from 'react';
import { useOrderRealtime } from './useOrderRealtime';
import { useToast } from '../contexts/ToastContext';
import { notificationHref } from '../utils/notificationDisplay';
import { isCentreConversationNotice, isNotificationForStore } from '../utils/notificationStoreScope';

interface UseNotificationRealtimeProps {
  storeId: number | null;
  accessToken: string | null;
  onNotificationReceived: (notification: any) => void;
  shouldShowToast?: (notification: any) => boolean; // Optional: determine if toast should be shown
  onPresenceUpdated?: (event: { storeId: number; users: any[] }) => void;
}

export const useNotificationRealtime = ({
  storeId,
  accessToken,
  onNotificationReceived,
  shouldShowToast,
  onPresenceUpdated
}: UseNotificationRealtimeProps) => {
  const { notify } = useToast();

  // Handle real-time notification received
  const handleNotificationReceived = useCallback((data: { notification: any; storeId: number }) => {
    const scoped = { ...data.notification, storeId: data.storeId };
    const forStore = isNotificationForStore(scoped, storeId);
    const centreNotice = isCentreConversationNotice(data.notification);
    if (!forStore && !centreNotice) {
      return;
    }

    console.log('🔔 [NOTIF GLOBAL] New notification received:', data.notification);
    
    // Check if toast should be shown (if function provided)
    const showToast = shouldShowToast ? shouldShowToast(data.notification) : true;
    
    // Show toast notification only if allowed
    if (showToast) {
      notify(
        data.notification.title,
        data.notification.message,
        'info',
        notificationHref(data.notification) || undefined
      );
    } else {
      console.log('🔕 [NOTIF GLOBAL] Toast suppressed (conversation open)');
    }
    
    if (forStore) {
      onNotificationReceived(data.notification);
    }
  }, [notify, onNotificationReceived, shouldShowToast, storeId]);

  // Use the existing order realtime hook for Socket.io connection
  const { isConnected, error } = useOrderRealtime({
    storeId,
    accessToken,
    onNotificationReceived: handleNotificationReceived,
    onPresenceUpdated
  });

  return {
    isConnected,
    error
  };
};

